/**
 * PocketFiler - Contract Templates Page
 * AI-powered legal document generation (NDA, SOW, MSA, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n';
import { UpgradePromptModal } from '../components/UsageWidget';
import {
  Shield, ShieldAlert, FileText, FileStack, UserCheck, Mail,
  ArrowLeft, Loader2, Download, Edit3, Trash2, Check, AlertCircle,
  ChevronRight, Lock, Eye, Sparkles, Plus
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const ICONS = {
  Shield, ShieldAlert, FileText, FileStack, UserCheck, Mail,
};

// ============================================
// TEMPLATE GALLERY
// ============================================

function TemplateCard({ template, onSelect }) {
  const { t } = useTranslation();
  const IconComp = ICONS[template.icon] || FileText;
  const categoryColors = {
    confidentiality: 'bg-blue-50 text-blue-700 border-blue-200',
    services: 'bg-green-50 text-green-700 border-green-200',
    employment: 'bg-purple-50 text-purple-700 border-purple-200',
    transactions: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <div
      onClick={() => template.available && onSelect(template)}
      className={`relative border rounded-xl p-5 transition-all cursor-pointer group ${
        template.available
          ? 'hover:border-primary hover:shadow-md bg-white'
          : 'opacity-60 cursor-not-allowed bg-gray-50'
      }`}
    >
      {!template.available && (
        <div className="absolute top-3 right-3">
          <Lock className="h-4 w-4 text-gray-400" />
        </div>
      )}
      <div className={`inline-flex p-2.5 rounded-lg border mb-3 ${categoryColors[template.category] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
        <IconComp className="h-5 w-5" />
      </div>
      <h3 className="font-semibold text-sm mb-1">{template.name}</h3>
      <p className="text-xs text-muted-foreground leading-relaxed">{template.description}</p>
      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
        <span>~{template.estimated_pages} pages</span>
        <span>{template.field_count} fields</span>
      </div>
      {template.available && (
        <div className="mt-3 flex items-center text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
          {t("contracts.generate") || "Generate"} <ChevronRight className="h-3 w-3 ml-1" />
        </div>
      )}
    </div>
  );
}


// ============================================
// FIELD FORM
// ============================================

function FieldForm({ template, onGenerate, onBack, generating }) {
  const { t } = useTranslation();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Set defaults
    const defaults = {};
    template.fields.forEach(f => {
      if (f.default !== undefined) defaults[f.key] = f.default;
    });
    setValues(defaults);
  }, [template]);

  const updateField = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: null }));
  };

  const handleSubmit = () => {
    const newErrors = {};
    template.fields.forEach(f => {
      if (f.required && !values[f.key] && values[f.key] !== 0) {
        newErrors[f.key] = t("contracts.required") || "Required";
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onGenerate(values);
  };

  const renderField = (field) => {
    const hasError = errors[field.key];
    const baseClass = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
      hasError ? 'border-red-400' : 'border-gray-200'
    }`;

    switch (field.type) {
      case 'select':
        return (
          <select
            className={baseClass}
            value={values[field.key] || ''}
            onChange={e => updateField(field.key, e.target.value)}
          >
            <option value="">Select...</option>
            {field.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'textarea':
        return (
          <textarea
            className={`${baseClass} min-h-[80px] resize-y`}
            value={values[field.key] || ''}
            onChange={e => updateField(field.key, e.target.value)}
            placeholder={field.placeholder || ''}
            rows={3}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            className={baseClass}
            value={values[field.key] || ''}
            onChange={e => updateField(field.key, e.target.value)}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            className={baseClass}
            value={values[field.key] || ''}
            onChange={e => updateField(field.key, parseFloat(e.target.value) || '')}
            min={field.min}
            max={field.max}
            placeholder={field.placeholder || ''}
          />
        );
      default:
        return (
          <input
            type="text"
            className={baseClass}
            value={values[field.key] || ''}
            onChange={e => updateField(field.key, e.target.value)}
            placeholder={field.placeholder || ''}
          />
        );
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to templates
      </button>

      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{template.name}</h2>
            <p className="text-sm text-muted-foreground">{template.description}</p>
          </div>
        </div>

        <div className="space-y-4">
          {template.fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderField(field)}
              {errors[field.key] && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors[field.key]}
                </p>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={generating}
          className="mt-6 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Generating document...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" /> Generate {template.short_name}
            </>
          )}
        </button>
      </div>
    </div>
  );
}


// ============================================
// DOCUMENT PREVIEW
// ============================================

function DocumentPreview({ draft, onBack, onCustomize, onFinalize, onDelete }) {
  const { t } = useTranslation();
  const [customizing, setCustomizing] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCustomize = async () => {
    if (!instructions.trim()) return;
    setLoading(true);
    await onCustomize(instructions);
    setInstructions('');
    setCustomizing(false);
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to templates
      </button>

      {/* Action bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border p-4 mb-4">
        <div>
          <h2 className="font-semibold">{draft.template_name}</h2>
          <p className="text-xs text-muted-foreground">Version {draft.version} • {draft.status}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCustomizing(!customizing)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Edit3 className="h-3.5 w-3.5" /> Modify
          </button>
          <button
            onClick={onFinalize}
            disabled={draft.status === 'finalized'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <Check className="h-3.5 w-3.5" /> Finalize
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* AI Customize */}
      {customizing && (
        <div className="bg-white rounded-xl border p-4 mb-4">
          <label className="block text-sm font-medium mb-2">{t("contracts.modifyPrompt") || "Tell AI how to modify this document:"}</label>
          <textarea
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-primary/30"
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            placeholder="e.g., Make the non-compete clause more restrictive, Add a force majeure clause..."
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => setCustomizing(false)}
              className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCustomize}
              disabled={loading || !instructions.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Apply Changes
            </button>
          </div>
        </div>
      )}

      {/* Document */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-8 md:p-12 prose prose-sm max-w-none" 
             dangerouslySetInnerHTML={{ __html: draft.generated_html }} 
        />
      </div>
    </div>
  );
}


// ============================================
// MAIN PAGE
// ============================================

export default function ContractTemplatesPage() {
  const { t } = useTranslation();
const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planInfo, setPlanInfo] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Load templates
  useEffect(() => {
    fetch(`${API}/api/contract-templates`, { headers })
      .then(r => r.json())
      .then(data => {
        setTemplates(data.templates || []);
        setPlanInfo({ plan: data.plan, monthlyLimit: data.monthly_limit });
        setLoading(false);
      })
      .catch(e => {
        setError(t("contracts.loadFailed") || "Failed to load templates");
        setLoading(false);
      });
  }, []);

  // Select template → load field definitions
  const handleSelect = async (template) => {
    try {
      const res = await fetch(`${API}/api/contract-templates/${template.key}`, { headers });
      const detail = await res.json();
      setTemplateDetail(detail);
      setSelectedTemplate(template);
      setCurrentDraft(null);
    } catch (e) {
      setError(t("contracts.detailsFailed") || "Failed to load template details");
    }
  };

  // Generate draft
  const handleGenerate = async (fieldValues) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/contract-templates/${selectedTemplate.key}/draft`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ field_values: fieldValues }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || t("contracts.generationFailed") || "Generation failed");
      }
      const draft = await res.json();
      setCurrentDraft(draft);
    } catch (e) {
      setError(e.message);
    }
    setGenerating(false);
  };

  // Customize draft
  const handleCustomize = async (instructions) => {
    try {
      const res = await fetch(`${API}/api/contract-templates/drafts/${currentDraft.id}/customize`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ draft_id: currentDraft.id, instructions }),
      });
      if (!res.ok) throw new Error('Customization failed');
      const updated = await res.json();
      setCurrentDraft(prev => ({
        ...prev,
        generated_html: updated.generated_html,
        version: updated.version,
      }));
    } catch (e) {
      setError(e.message);
    }
  };

  // Finalize draft
  const handleFinalize = async () => {
    try {
      const res = await fetch(`${API}/api/contract-templates/drafts/${currentDraft.id}/finalize`, {
        method: 'PATCH',
        headers,
      });
      if (!res.ok) throw new Error('Finalization failed');
      const result = await res.json();
      setCurrentDraft(prev => ({ ...prev, status: 'finalized', pdf_url: result.pdf_url }));
    } catch (e) {
      setError(e.message);
    }
  };

  // Delete draft
  const handleDelete = async () => {
    if (!window.confirm(t("contracts.confirmDelete") || "Delete this draft?")) return;
    try {
      await fetch(`${API}/api/contract-templates/drafts/${currentDraft.id}`, {
        method: 'DELETE',
        headers,
      });
      setCurrentDraft(null);
      setSelectedTemplate(null);
      setTemplateDetail(null);
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show document preview
  if (currentDraft) {
    return (
      <div className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}
        <DocumentPreview
          draft={currentDraft}
          onBack={() => {
            setCurrentDraft(null);
            setSelectedTemplate(null);
            setTemplateDetail(null);
          }}
          onCustomize={handleCustomize}
          onFinalize={handleFinalize}
          onDelete={handleDelete}
        />
      </div>
    );
  }

  // Show field form
  if (selectedTemplate && templateDetail) {
    return (
      <div className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}
        <FieldForm
          template={templateDetail}
          onGenerate={handleGenerate}
          onBack={() => {
            setSelectedTemplate(null);
            setTemplateDetail(null);
          }}
          generating={generating}
        />
      </div>
    );
  }

  // Show template gallery
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">{t("contracts.title") || "Contract Templates"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("contracts.subtitle") || "AI-powered legal document generation. Select a template to get started."}
          </p>
        </div>
        {planInfo.monthlyLimit !== -1 && (
          <span className="text-xs text-muted-foreground bg-gray-100 px-3 py-1 rounded-full">
            {planInfo.plan} plan • {planInfo.monthlyLimit} drafts/month
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(tmpl => (
          <TemplateCard key={tmpl.key} template={tmpl} onSelect={handleSelect} />
        ))}
      </div>
    </div>
  );
}
